import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/date";

export default function CustomerEnquiries() {
  usePageTitle("My Enquiries");
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [subject, setSubject] = useState("");
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const load = () => api.get("/enquiries?mine=true").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/enquiries", {
        name: user?.name || "Customer",
        email: user?.email || "",
        mobile: user?.phone || "",
        subject: subject || "New enquiry",
        message: msg,
      });
      setMsg(""); setSubject(""); setSent(true);
      setTimeout(() => setSent(false), 3000);
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="my-enquiries-root">
      <div className="border-b border-[hsl(var(--border))] pb-6">
        <div className="overline mb-2">Customer</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">My Enquiries</h1>
        <p className="text-sm text-muted-foreground mt-2">Raise a new enquiry or view your past ones. We respond within 2 business hours.</p>
      </div>

      <form onSubmit={submit} className="card-swiss p-5 space-y-3">
        <div className="overline">Raise New Enquiry</div>
        <input required value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (e.g. Custom flex banner 8x4)"
          className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="enq-subject-input" />
        <textarea required rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Describe what you need…"
          className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]" data-testid="enq-message-input" />
        {err && <div className="text-sm text-red-600">{err}</div>}
        {sent && <div className="text-sm text-emerald-600">Sent! We'll get back to you shortly.</div>}
        <button type="submit" className="btn-swiss btn-accent" data-testid="submit-enquiry-btn">
          <Send size={14} /> Send Enquiry
        </button>
      </form>

      <div className="space-y-2">
        <div className="overline">History</div>
        {rows.map((e) => (
          <div key={e.id} className="card-swiss p-4" data-testid={`enq-row-${e.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-semibold">{e.subject || "Enquiry"}</div>
              <div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">{e.status}</div>
            </div>
            <div className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{e.message}</div>
            <div className="text-xs text-muted-foreground font-mono mt-2">{fmtDateTime(e.created_at)}</div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="card-swiss p-8 text-center text-muted-foreground text-sm">No enquiries yet.</div>
        )}
      </div>
    </div>
  );
}
